import { useEffect, useState } from 'react';
import * as Dialog from '@radix-ui/react-dialog';
import { X, Save, Loader2 } from 'lucide-react';
import { Button } from '@/components/ui/Button';
import type { AdminRole, AdminUser } from '@/features/admin/types';
import { useCreateUser, useUpdateUser } from '@/features/admin/useAdmin';

interface Props {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  user?: AdminUser | null;       // when set -> edit mode
  roles: AdminRole[];
}

export function UserFormDialog({ open, onOpenChange, user, roles }: Props) {
  const isEdit = !!user;
  const create = useCreateUser();
  const update = useUpdateUser();
  const submitting = create.isPending || update.isPending;

  const [email, setEmail] = useState('');
  const [name, setName] = useState('');
  const [password, setPassword] = useState('');
  const [roleSlug, setRoleSlug] = useState('engineer');
  const [isActive, setIsActive] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!open) return;
    setError(null);
    if (user) {
      setEmail(user.email);
      setName(user.name);
      setPassword('');
      setRoleSlug(user.role.slug);
      setIsActive(user.is_active);
    } else {
      setEmail('');
      setName('');
      setPassword('');
      setRoleSlug(roles[0]?.slug ?? 'engineer');
      setIsActive(true);
    }
  }, [open, user, roles]);

  const onSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    try {
      if (isEdit && user) {
        await update.mutateAsync({
          id: user.id,
          data: {
            name,
            role_slug: roleSlug,
            is_active: isActive,
            ...(password ? { password } : {}),
          },
        });
      } else {
        await create.mutateAsync({
          email,
          name,
          password,
          role_slug: roleSlug,
          is_active: isActive,
        });
      }
      onOpenChange(false);
    } catch (e: any) {
      const detail = e?.response?.data?.detail;
      setError(typeof detail === 'string' ? detail : 'No se pudo guardar.');
    }
  };

  return (
    <Dialog.Root open={open} onOpenChange={onOpenChange}>
      <Dialog.Portal>
        <Dialog.Overlay className="fixed inset-0 z-50" style={{ background: 'rgba(2,6,23,0.75)', backdropFilter: 'blur(8px)' }} />
        <Dialog.Content
          className="fixed left-1/2 top-1/2 z-50 w-full max-w-md -translate-x-1/2 -translate-y-1/2 overflow-hidden rounded-[var(--radius-lg)] border shadow-2xl"
          style={{ background: 'var(--surface-1)', borderColor: 'var(--border-default)' }}
        >
          <form onSubmit={onSubmit}>
            <div className="flex items-center justify-between border-b px-5 py-3" style={{ borderColor: 'var(--border-subtle)' }}>
              <Dialog.Title className="text-base" style={{ fontFamily: 'var(--font-display)', fontWeight: 700 }}>
                {isEdit ? 'Editar usuario' : 'Nuevo usuario'}
              </Dialog.Title>
              <Dialog.Close asChild>
                <button type="button" aria-label="Cerrar" className="flex h-8 w-8 items-center justify-center rounded-[var(--radius-sm)] hover:bg-[var(--surface-2)]" style={{ color: 'var(--text-secondary)' }}>
                  <X size={14} />
                </button>
              </Dialog.Close>
            </div>

            <div className="space-y-3 px-5 py-4">
              <Field label="Correo electronico">
                <input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.currentTarget.value)}
                  required
                  disabled={isEdit}
                  className="h-9 w-full rounded-[var(--radius-sm)] border bg-[var(--surface-2)] px-3 text-sm focus:border-[var(--accent)] focus:outline-none disabled:opacity-60"
                  style={{ borderColor: 'var(--border-default)', color: 'var(--text-primary)' }}
                />
                {isEdit && (
                  <span className="mt-1 block text-[10px]" style={{ color: 'var(--text-muted)' }}>
                    El correo no se puede modificar.
                  </span>
                )}
              </Field>

              <Field label="Nombre">
                <input
                  type="text"
                  value={name}
                  onChange={(e) => setName(e.currentTarget.value)}
                  className="h-9 w-full rounded-[var(--radius-sm)] border bg-[var(--surface-2)] px-3 text-sm focus:border-[var(--accent)] focus:outline-none"
                  style={{ borderColor: 'var(--border-default)', color: 'var(--text-primary)' }}
                />
              </Field>

              <Field label={isEdit ? 'Nueva contrasena (opcional)' : 'Contrasena'}>
                <input
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.currentTarget.value)}
                  required={!isEdit}
                  minLength={isEdit && !password ? 0 : 8}
                  placeholder={isEdit ? 'Dejar vacio para no cambiar' : ''}
                  className="h-9 w-full rounded-[var(--radius-sm)] border bg-[var(--surface-2)] px-3 text-sm focus:border-[var(--accent)] focus:outline-none"
                  style={{ borderColor: 'var(--border-default)', color: 'var(--text-primary)' }}
                />
              </Field>

              <Field label="Rol">
                <select
                  value={roleSlug}
                  onChange={(e) => setRoleSlug(e.currentTarget.value)}
                  className="h-9 w-full rounded-[var(--radius-sm)] border bg-[var(--surface-2)] px-2 text-sm focus:border-[var(--accent)] focus:outline-none"
                  style={{ borderColor: 'var(--border-default)', color: 'var(--text-primary)' }}
                >
                  {roles.map((r) => (
                    <option key={r.slug} value={r.slug}>
                      {r.label} ({r.slug})
                    </option>
                  ))}
                </select>
              </Field>

              <label className="flex cursor-pointer items-center gap-2 text-xs" style={{ color: 'var(--text-secondary)' }}>
                <input type="checkbox" checked={isActive} onChange={(e) => setIsActive(e.currentTarget.checked)} className="h-4 w-4 accent-[var(--accent)]" />
                Cuenta activa
              </label>

              {error && (
                <div className="rounded-[var(--radius-sm)] border px-3 py-2 text-xs" style={{ background: 'rgba(239,68,68,0.10)', borderColor: 'var(--danger)', color: 'var(--danger)' }}>
                  {error}
                </div>
              )}
            </div>

            <div className="flex items-center justify-end gap-2 border-t px-5 py-3" style={{ borderColor: 'var(--border-subtle)', background: 'var(--surface-1)' }}>
              <Dialog.Close asChild>
                <Button type="button" variant="ghost" size="sm">
                  Cancelar
                </Button>
              </Dialog.Close>
              <Button type="submit" variant="primary" size="sm" disabled={submitting}>
                {submitting ? <Loader2 size={14} className="animate-spin" /> : <Save size={14} strokeWidth={1.8} />}
                {isEdit ? 'Guardar cambios' : 'Crear usuario'}
              </Button>
            </div>
          </form>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
}

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <label className="block">
      <span className="mb-1 block text-[10px] uppercase tracking-wider" style={{ color: 'var(--text-muted)' }}>
        {label}
      </span>
      {children}
    </label>
  );
}
